import React, { useState, useEffect } from "react";
import styled from "styled-components";
import DistributionPlot from "common/graphs/DistributionPlot";
import Switch from "@mui/material/Switch";
import { atBqnTarget } from "common/api";
import Loading from "common/Loading";
import { PERIOD_DASHBOARD_TO_HOURS, LOAD_INTERVAL, RETURN_VIEW } from "./utils";
import useDashboardData from "./useDashboardData";
import { parseApplicationMetrics } from "views/Statistics/DPI/ApplicationMetrics/api";

const Wrapper = styled.div`
  width: 100%;
`;

const Subtitle = styled.div`
  font-size: 12px !important;
  color: var(--text-primary);
  margin-top: 10px;
`;

const LayoutRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 750px) {
    flex-direction: column;
    align-items: flex-end;
    flex-wrap: wrap;

    .displaySelect {
      margin-right: 30px;
    }
  }
`;

const SwitchWrapper = styled.div`
  width: auto;
  display: flex;
  align-items: center;
  position: relative;
`;

const SwitchWithLabel = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const Label = styled.div`
  margin-right: 10px;
  font-family: AllianceNo2-SemiBold !important;
  font-weight: 100;
  color: var(--Text-Primary);
  width: 100px;
  text-align: right;
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 350px;
  color: var(--text-primary);
`;

const getApplicationMetricsData = ({ hours, mode, bqnId }) => {
  return ifCl.run(
    atBqnTarget(
      `show pkteng app-metrics hours ${hours} categories 10 subscriber all policy all subscriber-group all percentile`,
      mode,
      bqnId
    )
  );
};

function ApplicationMetrics({period}) {
  const [plotMode, setPlotMode] = useState("boxplot");
  const [showMinMax, setShowMinMax] = useState(false);
  const [filteredAppsDown, setFilteredAppsDown] = useState(new Set());

  const handleClick = (app) => {
    globalNavigate("viewDPIServiceMetrics", {returnView: RETURN_VIEW, selectedApp: app});
  }

  // The chart has a cadence of its own: it draws no range of the dashboard,
  // so nothing else asks for its data again.
  const { data, isLoading } = useDashboardData({
    retrieve: ({ mode, bqnId }) =>
      getApplicationMetricsData({
        hours: PERIOD_DASHBOARD_TO_HOURS[period],
        mode,
        bqnId,
      }).then((response) => {
        const parsedData = parseApplicationMetrics(response, mode, bqnId);

        return parsedData?.[bqnId]?.downlink ?? null;
      }),
    onError: "Error fetching application metrics data:",
    deps: [period],
    interval: LOAD_INTERVAL,
  });

  // Every application answered is drawn, until the one looking at the chart
  // takes some of them out of it: what came back names them again.
  useEffect(() => {
    setFilteredAppsDown(new Set((data || []).map((item) => item.app)));
  }, [data]);

  if (isLoading) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (!data || data.length === 0) {
    return <WrapperLoader>No data available</WrapperLoader>;
  }

  return (
    <Wrapper>
      <Subtitle>Downlink</Subtitle>
      <LayoutRow>
        <SwitchWrapper>
          <SwitchWithLabel>
            <Label>Line chart view</Label>{" "}
            <Switch
              className="sw-input"
              checked={plotMode !== "boxplot"}
              onChange={() =>
                setPlotMode(plotMode === "boxplot" ? "line" : "boxplot")
              }
            />
          </SwitchWithLabel>
          <SwitchWithLabel>
            <Label>View max+min</Label>{" "}
            <Switch
              className="sw-input"
              checked={showMinMax}
              onChange={() => setShowMinMax(!showMinMax)}
            />
          </SwitchWithLabel>
        </SwitchWrapper>
      </LayoutRow>
      <DistributionPlot
        plotMode={plotMode}
        data={data}
        selection={filteredAppsDown}
        setSelection={setFilteredAppsDown}
        minmax={showMinMax}
        yAxisUnit="Mbps"
        margin={{ top: 10, right: 50, bottom: 70, left: 60 }}
        onClick={(app) => handleClick(app)}
      />
    </Wrapper>
  );
}

export default React.memo(ApplicationMetrics);
